/** Property editor for the selected row/layout and its Bootstrap 12-column spans. */
import type { FormColumn, FormRow } from '../../models/form';
import { createId } from '../../utils/id';

interface Props {
  layout?: FormRow;
  onUpdate: (layout: FormRow) => void;
}

function distributeColumns(columns: FormColumn[]): FormColumn[] {
  const count = Math.max(1, columns.length);
  const base = Math.floor(12 / count);
  let remainder = 12 - base * count;
  return columns.map((column) => {
    const span = base + (remainder > 0 ? 1 : 0);
    remainder = Math.max(0, remainder - 1);
    return { ...column, span };
  });
}

function columnContentCount(column: FormColumn): number {
  const nestedRows = column.rows ?? [];
  return column.fields.length + nestedRows.length;
}

export function LayoutPropertyEditor({ layout, onUpdate }: Props) {
  if (!layout) {
    return <aside className="df-property-editor"><div className="df-empty-settings"><strong>لایه‌ای انتخاب نشده است</strong><p>روی حاشیه یک لایه کلیک کنید تا تنظیمات ستون‌ها نمایش داده شود.</p></div></aside>;
  }

  const totalSpan = layout.columns.reduce((total, column) => total + column.span, 0);
  const patchColumns = (columns: FormColumn[]) => onUpdate({ ...layout, columns });
  const addColumn = () => {
    if (layout.columns.length >= 12) return;
    patchColumns(distributeColumns([
      ...layout.columns,
      { id: createId('col'), span: 1, fields: [], rows: [] }
    ]));
  };
  const removeColumn = (columnId: string) => {
    if (layout.columns.length <= 1) return;
    const target = layout.columns.find((column) => column.id === columnId);
    const contentCount = target ? columnContentCount(target) : 0;
    if (contentCount > 0 && !window.confirm(`این ستون دارای ${contentCount} مورد است. محتوا به ستون اول منتقل شود؟`)) return;

    const remaining = layout.columns.filter((column) => column.id !== columnId);
    if (target && remaining[0]) {
      remaining[0] = {
        ...remaining[0],
        fields: [...remaining[0].fields, ...target.fields],
        rows: [...(remaining[0].rows ?? []), ...(target.rows ?? [])]
      };
    }
    patchColumns(distributeColumns(remaining));
  };

  return <aside className="df-property-editor">
    <div className="df-property-editor__heading"><div><strong>تنظیمات لایه</strong><small>{layout.columns.length} ستون · شناسه {layout.id}</small></div><span className="label label-primary">layout</span></div>
    <div className="df-property-list">
      <p className="help-block">عرض هر ردیف در Bootstrap برابر ۱۲ واحد است. مجموع کمتر از ۱۲ فضای خالی ایجاد می‌کند و مجموع بیشتر از ۱۲ باعث شکستن ستون‌ها به خط بعد می‌شود.</p>

      <div className={`df-layout-span-status ${totalSpan === 12 ? 'is-valid' : 'is-warning'}`}>
        <div><strong>مجموع عرض ستون‌ها</strong><span>{totalSpan} از ۱۲</span></div>
        <div className="df-layout-span-bar" aria-label={`مجموع عرض ${totalSpan} از ۱۲`}><i style={{ width: `${Math.min(100, (totalSpan / 12) * 100)}%` }} /></div>
        {totalSpan !== 12 ? <small>{totalSpan < 12 ? `${12 - totalSpan} واحد فضای خالی باقی مانده است.` : `${totalSpan - 12} واحد اضافه است و ممکن است ستون‌ها شکسته شوند.`}</small> : <small>چیدمان این لایه کامل است.</small>}
      </div>

      <div className="df-layout-editor-actions"><button type="button" className="btn btn-success" disabled={layout.columns.length >= 12} onClick={addColumn}>＋ افزودن ستون</button><button type="button" className="btn btn-info" onClick={() => patchColumns(distributeColumns(layout.columns))}>تقسیم مساوی</button></div>

      {layout.columns.map((column, index) => <div className="df-layout-column-setting" key={column.id}>
        <div className="df-layout-column-setting__header"><strong>ستون {index + 1}</strong><span className="badge">{columnContentCount(column)} مورد</span></div>
        <label>
          <span>عرض از ۱۲</span>
          <select
            className="form-control"
            value={column.span}
            onChange={(event) => patchColumns(layout.columns.map((item) => item.id === column.id ? { ...item, span: Number(event.target.value) } : item))}
          >
            {Array.from({ length: 12 }, (_, value) => value + 1).map((span) => <option key={span} value={span}>{span} واحد ({Math.round((span / 12) * 100)}٪)</option>)}
          </select>
        </label>
        <div className="df-column-mini-preview"><i style={{ width: `${(column.span / 12) * 100}%` }} /></div>
        <button type="button" className="btn btn-danger btn-xs" disabled={layout.columns.length <= 1} onClick={() => removeColumn(column.id)}>حذف ستون و انتقال محتوا</button>
      </div>)}
    </div>
  </aside>;
}
